import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../../../authContext';
import { useSecurity } from '../../../../../SecurityContext';
import usePersonalHubSectionSocket from '../hooks/usePersonalHubSectionSocket';
import { personalHubWsLastSeen } from '../api/personalHubWsApi';
import {
  getPersonalHubSessionBoot,
  personalHubSessionCacheKey,
  setPersonalHubSessionCache,
  PERSONAL_HUB_SECTION_HANGAR,
} from '../tools/personalHubSessionCache';
import { personalHubViewerCacheKey } from '../tools/personalHubViewerCacheKey';

function sortHangarsByMasterFirst(hangars) {
  return [...hangars].sort((a, b) => {
    if (a.isMaster && !b.isMaster) return -1;
    if (!a.isMaster && b.isMaster) return 1;
    return String(a.handle || '').localeCompare(String(b.handle || ''));
  });
}

export default function usePersonalHangarSession({ enabled = true } = {}) {
  const { isLoggedIn, userId } = useAuth();
  const { viewAsUserId, permissionsVersion } = useSecurity();

  const subjectUserId = viewAsUserId ?? userId;
  const viewerKey = personalHubViewerCacheKey({
    userId,
    isViewAsActive: viewAsUserId != null,
    viewAsUserId,
  });
  const cacheKey = personalHubSessionCacheKey(
    PERSONAL_HUB_SECTION_HANGAR,
    viewerKey,
    String(subjectUserId || '')
  );
  const sessionBoot = getPersonalHubSessionBoot(cacheKey);

  const [hangars, setHangars] = useState(() => sessionBoot?.hangars ?? []);
  const [allVehicles, setAllVehicles] = useState(() => sessionBoot?.allVehicles ?? []);
  const [activeHangarIndex, setActiveHangarIndex] = useState(
    () => sessionBoot?.activeHangarIndex ?? 0
  );
  const [loading, setLoading] = useState(() => enabled && !sessionBoot);
  const [pageFetching, setPageFetching] = useState(false);
  const [error, setError] = useState(sessionBoot?.error ?? null);
  const [shellVisible, setShellVisible] = useState(() => !!sessionBoot);

  const hasLoadedOnceRef = useRef(!!sessionBoot);
  const activeHangarIndexRef = useRef(activeHangarIndex);
  activeHangarIndexRef.current = activeHangarIndex;

  const persistCache = useCallback(
    (payload) => {
      setPersonalHubSessionCache(cacheKey, {
        hangars: payload.hangars ?? [],
        allVehicles: payload.allVehicles ?? [],
        activeHangarIndex: payload.activeHangarIndex ?? 0,
        error: payload.error ?? null,
      });
    },
    [cacheKey]
  );

  const loadHangar = useCallback(
    async ({ silent = false, preferredIndex = activeHangarIndexRef.current } = {}) => {
      if (!enabled || !isLoggedIn) return null;

      const hasCache = !!getPersonalHubSessionBoot(cacheKey);
      const isBlankVisit = !hasLoadedOnceRef.current && !hasCache;

      if (isBlankVisit) {
        setLoading(true);
      } else if (!silent) {
        setPageFetching(true);
      }
      setError(null);

      const [hangarRes, vehiclesRes] = await Promise.all([
        axios.post('/api/user/retrieve-personal-hangar', {}, { withCredentials: true }),
        axios.post('/api/user/retrieve-all-vehicles', {}, { withCredentials: true }),
      ]);

      const hangarDataRaw = Array.isArray(hangarRes.data) ? hangarRes.data : [hangarRes.data];
      const hangarData = sortHangarsByMasterFirst(hangarDataRaw);
      const vehicles = Array.isArray(vehiclesRes.data?.vehicles)
        ? vehiclesRes.data.vehicles
        : Array.isArray(vehiclesRes.data)
          ? vehiclesRes.data
          : [];

      const idx =
        preferredIndex >= 0 && preferredIndex < hangarData.length ? preferredIndex : 0;

      setHangars(hangarData);
      setAllVehicles(vehicles);
      setActiveHangarIndex(idx);
      setShellVisible(true);
      hasLoadedOnceRef.current = true;

      persistCache({
        hangars: hangarData,
        allVehicles: vehicles,
        activeHangarIndex: idx,
        error: null,
      });

      return null;
    },
    [cacheKey, enabled, isLoggedIn, persistCache]
  );

  useEffect(() => {
    if (!enabled || !isLoggedIn) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function run() {
      try {
        const hasCache = !!getPersonalHubSessionBoot(cacheKey);
        await loadHangar({ silent: hasCache });
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to fetch hangar', e);
        setError('Failed to load hangar');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setPageFetching(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, enabled, isLoggedIn, loadHangar, permissionsVersion]);

  useEffect(() => {
    hasLoadedOnceRef.current = !!getPersonalHubSessionBoot(cacheKey);
  }, [cacheKey]);

  const refetchHangar = useCallback(
    async ({ silent = true, preferredIndex } = {}) => {
      try {
        await loadHangar({ silent, preferredIndex: preferredIndex ?? activeHangarIndexRef.current });
      } catch (err) {
        console.error('[PersonalHub:hangar] refetch failed:', err);
      } finally {
        setLoading(false);
        setPageFetching(false);
      }
      return null;
    },
    [loadHangar]
  );

  const joinPayload = useMemo(
    () => (subjectUserId ? { subjectUserId: Number(subjectUserId) } : null),
    [subjectUserId]
  );

  usePersonalHubSectionSocket({
    section: PERSONAL_HUB_SECTION_HANGAR,
    joinEvent: 'joinPersonalHubHangar',
    leaveEvent: 'leavePersonalHubHangar',
    joinPayload,
    enabled: enabled && isLoggedIn,
    isLoggedIn,
    onRefetch: refetchHangar,
    resumeRegistryKey: `personalHub:hangar:${subjectUserId || userId || 0}`,
  });

  return {
    hangars,
    setHangars,
    allVehicles,
    activeHangarIndex,
    setActiveHangarIndex,
    loading,
    pageFetching,
    shellVisible,
    error,
    setError,
    refetchHangar,
    persistCache,
    cacheKey,
  };
}
